import React, { useEffect } from 'react';
import { Grid, Theme, Tab, alpha } from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';

import { ProductAdvantagePanel } from './ProductAdvantagePanel';

const products = [
  {
    name: 'Paraphraser',
    desc: 'The paraphraser does so much more than just reword referenced content--try it for fixing awkward paragraphs, improving weak topic sentences, and getting instant feedback and new ideas you can use to change up the structure, style, vocabulary, and tone of your work.',
    blogs: [
      {
        img: './images/theme/commonImages/studentResources/paraphraser-blog.png',
        title: 'What is Paraphrasing?',
        author: 'Dr. Emily Perry',
        authorImg:
          'https://www.gravatar.com/avatar/f52289eb1ed788014d492df1f3e69736?s=250&d=mm&r=x',
        date: 'May 2021',
        duration: '7 mins',
        url: 'https://quillbot.com/blog/what-is-paraphrasing/',
      },
      {
        img: './images/theme/commonImages/studentResources/paraphraser-guide.png',
        title: 'Paraphrasing Guide, Help Desk',
        date: 'June 2021',
        author: '',
        authorImg: '',
        duration: '5 mins',
        url: 'https://quillbot.com/help/en-us/categories/360005815253-Paraphraser-Guide',
      },
    ],
    video: {
      thumbnail: './images/theme/commonImages/studentResources/paraphraser-thumb.png',
      videoUrl: 'https://www.youtube.com/embed/tbnUqvXsCWs?rel=0',
    },
  },
  {
    name: 'Grammar Checker',
    desc: 'When you’ve worked on a paper for so long and feel like you just can’t read it even one last time to check for any outstanding errors, use the grammar checker for low energy editing and polishing. Think we’d just cover grammar? That’s not our style. This tool will catch other writing errors like word misuse, incorrect spelling, and punctuation mistakes.',
    blogs: [
      {
        img: './images/theme/commonImages/studentResources/grammar-blog.png',
        title: 'What is Hyphen?',
        author: 'Page Pfeifer',
        authorImg:
          'https://quillbot.com/blog/assets/images/default-avatar-square-normal.jpg?v=006d039052',
        date: 'June 2021',
        duration: '5 mins',
        url: 'https://quillbot.com/blog/what-is-a-hyphen/',
      },
      {
        img: './images/theme/commonImages/studentResources/grammar-blog-2.png',
        title: 'Affect vs. Effect',
        author: 'Dr. Emily Perry',
        authorImg:
          'https://www.gravatar.com/avatar/f52289eb1ed788014d492df1f3e69736?s=250&d=mm&r=x',
        date: 'June 2021',
        duration: '5 mins',
        url: 'https://quillbot.com/blog/affect-vs-effect/',
      },
    ],
    video: {
      thumbnail: './images/theme/commonImages/studentResources/grammar_checker-thumb.png',
      videoUrl: 'https://www.youtube.com/embed/P0FgWfmRYRQ?rel=0',
    },
  },
  {
    name: 'Summarizer',
    desc: 'The summarizer can, of course, be used to keep up with news and extra-long emails, but it can also be used to check reading comprehension and summarizing skills for students looking to enhance their English fluency or get familiar with working in dense or tricky types of writing, such as academic or technical writing.',
    blogs: [
      {
        img: './images/theme/commonImages/studentResources/summarizer-blog.png',
        title: 'What is Summary?',
        author: 'Page Pfeifer',
        authorImg:
          'https://quillbot.com/blog/assets/images/default-avatar-square-normal.jpg?v=006d039052',
        date: 'May 2021',
        duration: '5 mins',
        url: 'https://quillbot.com/blog/what-is-a-summary/',
      },
      {
        img: './images/theme/commonImages/studentResources/summarizer-guide.png',
        title: 'Summarizer Guide, Help Desk',
        author: '',
        authorImg: '',
        date: 'May 2021',
        duration: '5 mins',
        url: 'https://quillbot.com/help/en-us/categories/360005817153-Summarizer-Guide',
      },
    ],
    video: {
      thumbnail: './images/theme/commonImages/studentResources/summarizer-thumb.png',
      videoUrl: 'https://www.youtube.com/embed/ThzZa-jPZek?rel=0',
    },
  },
  {
    name: 'Citation Generator',
    desc: 'Save your brain power for building better arguments--use QuillBot’s new citation generator tool to get perfect APA, MLA, and Chicago style citations in one click. No ads, no frustrating reloading pages, and no annoying new tabs opening everywhere.',
    blogs: [
      {
        img: './images/theme/commonImages/studentResources/citation-blog.png',
        title: 'Announcing QuillBot’s New Citation Generator',
        author: 'Dr. Emily Perry',
        authorImg:
          'https://www.gravatar.com/avatar/f52289eb1ed788014d492df1f3e69736?s=250&d=mm&r=x',
        date: 'June 2021',
        duration: '4 mins',
        url: 'https://quillbot.com/blog/announcing-quillbots-new-citation-generator/',
      },
      {
        img: './images/theme/commonImages/studentResources/citation-blog-2.png',
        title: 'How to Cite Sources - APA, MLA, and Chicago',
        author: 'Page Pfeifer',
        authorImg:
          'https://quillbot.com/blog/assets/images/default-avatar-square-normal.jpg?v=006d039052',
        date: 'Aug 2021',
        duration: '8 mins',
        url: 'https://quillbot.com/blog/how-to-cite-sources-apa-mla-and-chicago/',
      },
    ],
    video: {
      thumbnail: './images/theme/commonImages/studentResources/citation_generator-thumb.png',
      videoUrl: 'https://www.youtube.com/embed/xgxhikNghcI?rel=0',
    },
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  headerWrapper: {
    backgroundColor: theme.specialColors.CHECKOUT_PAGE_BG,
  },
  header: {
    padding: '32px 16px',
    maxWidth: 1142,
    margin: '0 auto',
    textAlign: 'center',
  },
  pageTitle: {
    fontSize: 30,
    fontWeight: 400,
    maxWidth: 640,
    margin: '0 auto',
    textAlign: 'center',
  },
  tabsHeadWrapper: {
    margin: '24px -16px 0',
  },
  tabPill: {
    color: theme.specialColors.ICON_DEFAULT_COLOR,
    opacity: 1,
    textTransform: 'none',
    fontSize: 16,
    lineHeight: '21.79px',
    minHeight: 'initial',
    padding: '10px 16px',
    backgroundColor: theme.specialColors.THREE_BEAM,
    borderRadius: 100,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
    minWidth: 'initial',
    transition: 'all 0.2s ease-in-out',
    marginRight: 16,
    '&:last-child': {
      marginRight: 0,
    },
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  tabSelected: {
    fontWeight: 600,
    color: `${theme.specialColors.THREE_BEAM} !important`,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      color: `${theme.specialColors.THREE_BEAM} !important`,
    },
  },
  tabIndicator: {
    display: 'none',
  },
  tabFlexContainer: {
    maxWidth: '100vw',
    overflowX: 'auto',
    padding: '0 16px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  tabsContentWrapper: {
    maxWidth: 1142,
    margin: '0 auto',
  },
  tabsContent: {},
  tabPanel: {
    padding: '32px 0 0',
    marginBottom: '64px',
  },
}));

export default function ProductsAdvantage(props: { selectTab: string }) {
  const isMobile = useSelector((rootState: RootState) => rootState.common.platform.isMobile);
  const classes = useStyles();
  const [value, setValue] = React.useState('Paraphraser');

  useEffect(() => {
    setValue(props.selectTab);
  }, [props.selectTab]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setValue(newValue);
  };

  return (
    <>
      <TabContext value={value}>
        <Grid container className={classes.headerWrapper}>
          <Grid item className={classes.header}>
            <h2 className={classes.pageTitle}>
              Students gain a competitive advantage when building their skills with the QuillBot
              tools.
            </h2>
            <div className={classes.tabsHeadWrapper}>
              <TabList
                onChange={handleChange}
                centered={!isMobile}
                // variant="scrollable"
                // scrollableButtons="auto"
                classes={{
                  indicator: classes.tabIndicator,
                  flexContainer: classes.tabFlexContainer,
                }}
              >
                {products.map((product) => (
                  <Tab
                    key={product.name}
                    label={product.name}
                    value={product.name}
                    classes={{
                      root: classes.tabPill,
                      selected: classes.tabSelected,
                    }}
                  />
                ))}
              </TabList>
            </div>
          </Grid>
        </Grid>

        <Grid container className={classes.tabsContentWrapper}>
          <Grid item className={classes.tabsContent}>
            {products.map((product) => (
              <TabPanel key={product.name} value={product.name} className={classes.tabPanel}>
                <ProductAdvantagePanel product={product} />
              </TabPanel>
            ))}
          </Grid>
        </Grid>
      </TabContext>
    </>
  );
}
